import React, { useEffect, useState } from "react";

import { BlockSkeleton } from "./BlockSkeleton";

interface Props {
  blockId: string;
  imageUrl: string;
  aiPrompt: string;
  idSuffix?: string;
  isRegenerating?: boolean;
}

export const AIImageBlock: React.FC<Props> = ({
  blockId,
  imageUrl,
  aiPrompt,
  idSuffix = "",
  isRegenerating = false,
}) => {
  const fullBlockId = `block_${blockId}${idSuffix ? `_${idSuffix}` : ""}`;
  const hasImageUrl = Boolean(imageUrl && imageUrl !== "None");
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
  }, [imageUrl]);

  if (!hasImageUrl) {
    return (
      <div
        id={fullBlockId}
        className={(isRegenerating ? "ai-image-regenerating-wrapper " : "") + "h-full"}
      >
        <BlockSkeleton blockId={blockId} blockType="ai_image" />
      </div>
    );
  }

  return (
    <div
      id={fullBlockId}
      className={(isRegenerating ? "ai-image-regenerating-wrapper " : "") + "h-full"}
    >
      <div className="themed-dashboard-card !p-0 h-full">
        <div
          className="relative w-full flex-1 min-h-0 flex items-center justify-center overflow-hidden rounded-themed"
          data-sentry-block
          style={{
            backgroundColor: "var(--theme--color-ai-image-background, #DEE9FE)",
            boxShadow: "var(--theme--shadow-ai-image-inset, inset 0px 0px 22px 22px #DEE9FE)",
          }}
        >
          {!isImageLoaded && (
            <div className="absolute inset-0">
              <BlockSkeleton blockId={blockId} blockType="ai_image" />
            </div>
          )}
          {/* Always show the stale image while regenerating */}
          <img
            src={imageUrl}
            alt={aiPrompt}
            onLoad={() => setIsImageLoaded(true)}
            className={`max-w-full max-h-full rounded-themed object-contain transition-opacity duration-300 ${
              isImageLoaded ? "opacity-100" : "opacity-0"
            }`}
          />

          {isRegenerating && (
            <>
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-lg">
                <span className="h-2 w-2 rounded-full bg-[#3a5cff] animate-pulse" />
                Updating image
              </div>
              {/* Top-right status icon with tooltip */}
              <div className="absolute top-3 right-3">
                <div className="group relative">
                  <button
                    type="button"
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-white/90 shadow-md text-gray-700 hover:bg-white"
                    aria-label="Image is updating with the latest data"
                  >
                    <span className="material-icons !text-base">info</span>
                  </button>
                  <div className="pointer-events-none absolute right-0 mt-2 w-64 rounded-md bg-gray-900 text-white text-xs px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    Image is updating with the latest data. This may take a few moments.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
